import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { Logo } from "@/components/shared/Logo";
import { CheckCircle2, XCircle, AlertTriangle, Home, Wallet, LayoutDashboard, Compass } from "lucide-react";

const CONFIG = {
  success: {
    icon: CheckCircle2, color: "text-emerald-500", ring: "bg-emerald-500/10",
    titleKey: ["pay.success.title", "Paiement reçu"],
    descKey: ["pay.success.desc", "Merci ! Votre paiement a bien été enregistré. Votre solde sera crédité automatiquement dès la confirmation du prestataire."],
  },
  cancel: {
    icon: AlertTriangle, color: "text-amber-500", ring: "bg-amber-500/10",
    titleKey: ["pay.cancel.title", "Paiement annulé"],
    descKey: ["pay.cancel.desc", "Vous avez annulé le paiement. Aucun montant n'a été débité. Vous pouvez réessayer quand vous le souhaitez."],
  },
  failed: {
    icon: XCircle, color: "text-red-500", ring: "bg-red-500/10",
    titleKey: ["pay.failed.title", "Paiement échoué"],
    descKey: ["pay.failed.desc", "Le paiement n'a pas pu aboutir. Aucun montant n'a été crédité. Merci de réessayer ou de contacter le support."],
  },
};

export function PaymentResult({ kind = "success" }) {
  const { user } = useAuth();
  const { t } = useContent();
  const [sp] = useSearchParams();
  const statusParam = (sp.get("status") || "").toLowerCase();
  const resolved = ["success", "cancel", "failed"].includes(statusParam) ? statusParam : kind;
  const cfg = CONFIG[resolved] || CONFIG.success;
  const Icon = cfg.icon;
  const reference = sp.get("reference") || sp.get("ref") || sp.get("transaction_ref") || sp.get("order_id") || sp.get("id");

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-background px-5 py-12" data-testid={`payment-result-${resolved}`}>
      <Link to="/" className="mb-8 flex items-center gap-2">
        <Logo alt="The World Promotions" className="h-9 w-9 [filter:brightness(0)] dark:[filter:brightness(0)_invert(1)]" />
        <span className="font-display text-lg font-bold tracking-tight">The World Promotions</span>
      </Link>

      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
        <div className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full ${cfg.ring}`}>
          <Icon className={`h-9 w-9 ${cfg.color}`} data-testid="payment-result-icon" />
        </div>
        <h1 className="font-display text-2xl font-bold" data-testid="payment-result-title">{t(cfg.titleKey[0], cfg.titleKey[1])}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{t(cfg.descKey[0], cfg.descKey[1])}</p>

        {reference && (
          <div className="mt-5 rounded-lg border border-border bg-secondary/40 px-4 py-3 text-sm" data-testid="payment-result-ref">
            <span className="text-muted-foreground">{t("pay.reference", "Référence : ")}</span>
            <span className="font-mono font-medium break-all">{reference}</span>
          </div>
        )}

        <div className="mt-7 flex flex-col gap-2">
          {user ? (
            <>
              <Link to="/dashboard/transactions" data-testid="payment-result-transactions" className="flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-accent"><LayoutDashboard className="h-4 w-4" /> {t("pay.view_txn", "Voir mes transactions")}</Link>
              {resolved !== "success" && (
                <Link to="/dashboard/add-funds" data-testid="payment-result-retry" className="flex items-center justify-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-medium hover:bg-secondary"><Wallet className="h-4 w-4" /> {t("pay.retry", "Réessayer le paiement")}</Link>
              )}
            </>
          ) : (
            <Link to="/login" data-testid="payment-result-login" className="flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-accent"><LayoutDashboard className="h-4 w-4" /> {t("pay.login", "Se connecter")}</Link>
          )}
          <Link to="/" data-testid="payment-result-home" className="flex items-center justify-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-medium hover:bg-secondary"><Home className="h-4 w-4" /> {t("pay.home", "Retour à l'accueil")}</Link>
        </div>
      </div>
    </div>
  );
}

export function NotFound() {
  const { t } = useContent();
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-background px-5 py-12 text-center" data-testid="not-found-page">
      <Link to="/" className="mb-8 flex items-center gap-2">
        <Logo alt="The World Promotions" className="h-9 w-9 [filter:brightness(0)] dark:[filter:brightness(0)_invert(1)]" />
        <span className="font-display text-lg font-bold tracking-tight">The World Promotions</span>
      </Link>
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
        <p className="font-display text-6xl font-bold text-accent">404</p>
        <h1 className="mt-3 font-display text-xl font-bold">{t("notfound.title", "Page introuvable")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("notfound.desc", "La page que vous cherchez n'existe pas ou a été déplacée.")}</p>
        <div className="mt-6 flex flex-col gap-2">
          <Link to="/" data-testid="notfound-home" className="flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-accent"><Home className="h-4 w-4" /> {t("notfound.home", "Accueil")}</Link>
          <Link to="/services" data-testid="notfound-services" className="flex items-center justify-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-medium hover:bg-secondary"><Compass className="h-4 w-4" /> {t("notfound.services", "Voir les services")}</Link>
        </div>
      </div>
    </div>
  );
}
